import { Tool } from "ai";
import { useMemo } from "react";
import { matchPath, useNavigate } from "react-router";
import * as z from "zod";

import { TOOL_CALL_DESCRIPTION_FIELD } from "components/AiChat/constants";
import { ArrowRight } from "components/icons/generated";

import { NAVIGATION_ROUTES, NavigationRoute } from "./routes.generated";

const formatAvailability = (route: NavigationRoute): string => {
  if (!route.availability.length) return "";
  return ` (available when: ${route.availabilityExpression ?? route.availability.join(" && ")})`;
};

const buildToolDescription = (): string => {
  const lines = NAVIGATION_ROUTES.map((r) => `- ${r.pattern} — ${r.label}${formatAvailability(r)}`);

  return [
    "Navigate to a page in the application. Replace any `:param` placeholders with real values",
    "(use IDs from getPageContext or values the user provided). Submit only paths whose pattern",
    "matches one of the entries below.",
    "",
    "Supported routes:",
    ...lines,
  ].join("\n");
};

const navigationInputSchema = z.object({
  [TOOL_CALL_DESCRIPTION_FIELD]: z
    .string()
    .describe(
      "A short, user-friendly description of the action. Examples: 'Opening the stacks list', 'Going to project settings', 'Navigating to stack details'"
    ),
  path: z
    .string()
    .describe(
      "Absolute URL path to navigate to. Must resolve against one of the supported route patterns."
    ),
});

type NavigationInput = z.infer<typeof navigationInputSchema>;

type NavigationOutput = { success: true; navigatedTo: string } | { success: false; reason: string };

const findMatchingRoute = (path: string): NavigationRoute | undefined =>
  NAVIGATION_ROUTES.find((r) => matchPath({ path: r.pattern, end: false }, path) !== null);

export const toolIcon = ArrowRight;

const useNavigationTool = (): Tool<NavigationInput, NavigationOutput> => {
  const navigate = useNavigate();

  return useMemo(
    () => ({
      title: "Navigating to page",
      description: buildToolDescription(),
      inputSchema: navigationInputSchema,
      execute: async ({ path }) => {
        const matched = findMatchingRoute(path);
        if (!matched) {
          return {
            success: false,
            reason: `Path "${path}" does not match any supported route pattern.`,
          };
        }
        navigate(path);
        return { success: true, navigatedTo: path };
      },
    }),
    [navigate]
  );
};

export default useNavigationTool;
